<template>
    <!--            &#45;&#45; Tailwind UI -> Data Display -> Description Lists -> Left-aligned in card &#45;&#45;-->
    <div class="w-full bg-white border border-t-0 border-gray-200 sm:rounded-b-lg">
        <div class="px-4 py-5 border-b border-gray-200 sm:px-6">
            <p class="mt-1 text-sm leading-5 text-gray-500">
                Alarms can be snoozed to disable notifications to users for a specified period of time.
            </p>
            <p class="mt-1 text-sm leading-5 text-gray-500">
                By snoozing this alarm, no new alarms will be created and no notifications will occur until
                either the alarm is resolved or the snooze expires.
            </p>
            <p v-if="isSnoozed && massMode" class="mt-1 text-sm leading-5 text-gray-500">
                Some selected alarms are already snoozed. This will replace their current snooze; no one is notified.
            </p>
            <p v-else-if="isSnoozed" class="mt-1 text-sm leading-5 text-gray-500">
                This alarm is already snoozed. This will replace the current snooze; no one is notified.
            </p>
        </div>
        <div class="px-4 pt-5 pb-0 sm:pb-5 sm:p-0">
            <dl>
                <div class="sm:grid sm:grid-cols-4 sm:gap-6 sm:px-6 sm:py-5">
                    <dt class="text-sm leading-5 font-medium text-gray-500">
                        How long to snooze this alarm?
                        <p v-if="isCapped" class="text-base pt-4 text-gray-500">
                            <i>{{ policyText('snoozeLimitNote') }}</i>
                        </p>
                        <p v-else class="text-base pt-4 text-gray-500">
                            <i>Silence Alarm</i> will snooze the alarm indefinitely.
                        </p>
                    </dt>
                    <dd class="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-3">
                        <!--                                &#45;&#45; Tailwind UI -> Elements -> Button Groups -> Basic&#45;&#45;-->
                        <span class="relative z-0 flex mb-2">
                              <button type="button" @click="snoozeTimeClick"
                                      value="1"
                                      :class="{'bg-blue-100': snoozeTime === '1', 'bg-white' : snoozeTime !== '1'}"
                                      class="w-1/3 items-center px-4 py-2 rounded-l-md border border-gray-300 text-sm leading-5 text-lg font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue-200 active:bg-gray-100 active:text-gray-700">
                                1hr
                              </button>
                              <button type="button" @click="snoozeTimeClick"
                                      value="6"
                                      :class="{'bg-blue-100': snoozeTime === '6', 'bg-white' : snoozeTime !== '6'}"
                                      class="-ml-px w-1/3 items-center px-4 py-2 border border-gray-300 text-sm leading-5 text-lg font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue-200 active:bg-gray-100 active:text-gray-700">
                                6hr
                              </button>
                              <button type="button" @click="snoozeTimeClick"
                                      value="24"
                                      :class="{'bg-blue-100': snoozeTime === '24', 'bg-white' : snoozeTime !== '24'}"
                                      class="-ml-px w-1/3 items-center px-4 py-2 rounded-r-md border border-gray-300 text-sm leading-5 text-lg font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue-200 active:bg-gray-100 active:text-gray-700">
                                24hr
                              </button>
                            </span>
                        <span class="relative z-0 flex mb-2">
                              <button type="button" @click="snoozeTimeClick"
                                      v-if="showTomorrow"
                                      value="tomorrow"
                                      :class="[{'bg-blue-100': snoozeTime === 'tomorrow', 'bg-white': snoozeTime !== 'tomorrow'}, showNextMonday ? 'w-1/2 rounded-l-md' : 'w-full rounded-md']"
                                      class="items-center px-4 py-2 border border-gray-300 text-sm leading-5 text-lg font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue-200 active:bg-gray-100 active:text-gray-700">
                                Tomorrow Morning
                              </button>
                              <button type="button" @click="snoozeTimeClick"
                                      v-if="showNextMonday"
                                      value="monday"
                                      :class="[{'bg-blue-100': snoozeTime === 'monday', 'bg-white' : snoozeTime !== 'monday'}, showTomorrow ? '-ml-px w-1/2 rounded-r-md' : 'w-full rounded-md']"
                                      class="items-center px-4 py-2 border border-gray-300 text-sm leading-5 text-lg font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue-200 active:bg-gray-100 active:text-gray-700">
                                Next Monday
                              </button>
                            </span>
                        <span class="relative z-0 flex mb-2">
                              <button type="button"
                                      @click="snoozeTimeClick"
                                      value="custom"
                                      :class="[{'bg-blue-100': snoozeTime === 'custom', 'bg-white': snoozeTime !== 'custom'}, isCapped ? 'w-full rounded-md' : 'w-1/2 rounded-l-md']"
                                      class="items-center px-4 py-2 border border-gray-300 text-sm leading-5 text-lg font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue-200 active:bg-gray-100 active:text-gray-700">
                                Custom Duration
                              </button>
                              <button type="button" @click="snoozeTimeClick"
                                      v-if="!isCapped"
                                      value="silence"
                                      :class="{'bg-blue-100': snoozeTime === 'silence', 'bg-white' : snoozeTime !== 'silence'}"
                                      class="-ml-px w-1/2 items-center px-4 py-2 rounded-r-md border border-gray-300 text-sm leading-5 text-lg font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue-200 active:bg-gray-100 active:text-gray-700">
                                Silence Alarm
                              </button>
                        </span>
                        <p v-if="isCapped" class="text-base text-gray-500 italic mb-2">
                            {{ policyText('silenceDisabled') }}
                        </p>
                        <span class="relative z-0 flex mb-2" v-show="showDatetime">
                            <date-time-picker   class="grow"
                                                v-model="datetime"
                                                :min-date="minDate"
                                                :max-date="maxDate"
                                                :side-by-side="true"
                                                vertical-position="top"
                                                :timezone="timezone"
                                                format="MM/DD/YYYY HH:mm"></date-time-picker>
                        </span>
                        <p v-if="isCapped && showDatetime" class="text-base text-gray-500 italic mb-2">
                            {{ policyText('snoozeCapped') }}
                        </p>
                    </dd>
                </div>

                <!-- Comments for snooze. -->
                <div class="mt-2 sm:mt-0 sm:grid sm:grid-cols-4 sm:gap-6 sm:border-t sm:border-gray-200 sm:px-6 sm:py-5">
                    <dt class="text-sm leading-5 font-medium text-gray-500">
                        Comments<span v-if="requireComment" class="text-red-500 ml-0.5">*</span>
                        <p v-if="requireComment" class="mt-1 text-base font-normal text-gray-500">
                            {{ policyText('requireComment') }}
                        </p>
                    </dt>
                    <dd class="mt-1 text-sm leading-5 text-gray-900 sm:mt-0 sm:col-span-3">
                        <textarea v-model="comments" class="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6" rows="4" placeholder="Enter any comments here"></textarea>
                    </dd>
                </div>
            </dl>
        </div>

        <!-- Button to snooze alarms. -->
        <div class="px-4 pb-5 pt-2 sm:pt-5 sm:border-t mt-2 p-1">
            <div    v-if="errorsSnoozing !== {}"
                    class="pb-4">
                <!-- Error Banner -->
                <banner-error   v-for="(error, errorTitle) in errorsSnoozing">
                    <template   #title>
                        {{ errorTitle }}
                    </template>

                    <template   #content>
                        {{ error }}
                    </template>
                </banner-error>

            </div>
            <div class="flex sm:justify-end">
                <dl class="mb-0 w-full sm:w-auto">
                    <span class="mt-3 inline-flex w-full rounded-md sm:mt-0 sm:w-auto" :class="submitDisabled ? 'cursor-not-allowed' : ''">
                        <button type="button"
                                @click="sendFormData"
                                :disabled="submitDisabled"
                                :class="submitDisabled ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-500 active:bg-blue-700 cursor-pointer'"
                                class="w-full inline-flex items-center justify-center min-w-80 px-4 py-2 border border-transparent font-medium rounded-md text-white focus:outline-none focus:border-blue-700 focus:ring-indigo-200 sm:w-auto sm:text-sm sm:leading-5">
                          Snooze Alarm
                        </button>
                    </span>
                </dl>
            </div>
        </div>
    </div>
</template>

<script>
    import DateTimePicker from "@/components/Reports/VueDatePicker";
    import { DateTime as LuxonDatetime}  from 'luxon';
    import axios from 'axios';
    import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
    import BannerError from "@/components/Banners/BannerError.vue";
    import { alarmPolicyText } from "@/support/alarmPolicyCopy";
    export default {
        name: "alarmAddressSnooze",
        mixins: [SonicuCoreMixin],
        components: {DateTimePicker, BannerError},
        props: {
            timezone: {
                type: String,
                default: 'utc',
            },
            sendEventOnly: Boolean,
            requireComment: {
                type: Boolean,
                default: false,
            },
            maxSnoozeDays: {
                type: Number,
                default: null,
            },
            isSnoozed: {
                type: Boolean,
                default: false,
            },
            massMode: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                showDatetime: false,
                luxDateTime: LuxonDatetime.utc().plus({days: 7, hours: 1}),
                datetime: null,
                comments: null,
                snoozeTime: null,
                errorsSnoozing: {},
            }
        },
        mounted() {
            this.luxDateTime = this.luxDateTime.setZone(this.timezone)
            // The default seed is now + 7 days. If the site caps snoozes below that, clamp the
            // default to the cap cutoff so the picker never pre-fills an out-of-range date.
            if (this.capLimit && this.luxDateTime.toMillis() > this.capLimit.toMillis()) {
                this.luxDateTime = this.capLimit
            }
            this.datetime = this.luxDateTime.toJSDate()
        },
        computed: {
            anythingSelected() {
                return this.snoozeTime !== null;
            },
            minDate(){
                let time = LuxonDatetime.utc()
                return time.setZone(this.timezone).toJSDate()
            },
            commentFilled() {
                return !!this.comments && this.comments.trim().length > 0;
            },
            submitDisabled() {
                return !this.anythingSelected || (this.requireComment && !this.commentFilled);
            },
            isCapped() {
                return this.maxSnoozeDays !== null;
            },
            tomorrowMorning() {
                // Mirrors backend _calculateSnoozetime('tomorrow'): tomorrow at 09:05 local.
                // Use utc().setZone(tz) for "now in tz" to match this file's existing pattern
                // (Luxon in this project has no DateTime.now() factory).
                return LuxonDatetime.utc()
                    .setZone(this.timezone)
                    .plus({ days: 1 })
                    .set({ hour: 9, minute: 5, second: 0, millisecond: 0 });
            },
            nextMonday() {
                // Mirrors backend _calculateSnoozetime('monday'): next Monday at 09:05 local.
                // Luxon startOf('week') is Monday 00:00, same as Carbon startOfWeek().
                return LuxonDatetime.utc()
                    .setZone(this.timezone)
                    .startOf('week')
                    .plus({ weeks: 1 })
                    .set({ hour: 9, minute: 5, second: 0, millisecond: 0 });
            },
            // The cap cutoff is the END of the (now + maxSnoozeDays) day, so the entire
            // final day is selectable in the custom picker. This mirrors the backend's
            // snoozeCapViolation() check (which also compares against end-of-day of the cap)
            // so the UI never offers a preset or custom time the backend would reject with a 422.
            capLimit() {
                if (!this.isCapped) {
                    return null;
                }
                return LuxonDatetime.utc()
                    .setZone(this.timezone)
                    .plus({ days: this.maxSnoozeDays })
                    .endOf('day');
            },
            showTomorrow() {
                return !this.isCapped || this.tomorrowMorning.toMillis() <= this.capLimit.toMillis();
            },
            showNextMonday() {
                return !this.isCapped || this.nextMonday.toMillis() <= this.capLimit.toMillis();
            },
            maxDate() {
                return this.capLimit ? this.capLimit.toJSDate() : null;
            },
        },
        methods: {
            policyText(key) {
                return alarmPolicyText(key, { maxSnoozeDays: this.maxSnoozeDays });
            },
            snoozeTimeClick(event){
                if(event.target.value.localeCompare("custom") === 0){
                    this.showDatetime = true;
                } else {
                    // You must use 'this' or it won't update
                    this.showDatetime = false;
                }
                this.snoozeTime = event.target.value;
            },
            sendFormData(){
                this.errorsSnoozing = {}
                // Get the datetime independent of timezone (despite timezone being tied to the var) and we will format it to iso for the request.
                let zoneLessDatetime = LuxonDatetime.fromJSDate(this.datetime)
                    .setZone('utc', {keepLocalTime: true})

                let data = {
                    'addressed': 'snooze',
                    'snooze_time': this.snoozeTime,
                    // This ISO String should be formatted as UTC, but be local time.
                    'datetime': zoneLessDatetime,
                    'comments': this.comments,
                }

                if (this.sendEventOnly) {
                    // If we're only sending the event, emit the event and return
                    this.$emit('snoozing-alarm', data)
                    return
                }

                axios.patch(window.location.pathname, data
                ).then(response => {
                    let data = response.data
                    if (_.size(data.errors) > 0) {
                        // If errors occured, display errors, then after 5 seconds, reload the page
                        this.errorsSnoozing = data.errors;
                        setTimeout(() => {
                            window.location.reload()
                        }, 10000);
                        return
                    }

                    // No errors occured
                    window.location.href = this.company.buildCompanyRoute('alarms');
                    return false;
                }).catch(error => {
                    if (error.response && error.response.data && error.response.data.errors) {
                        this.errorsSnoozing = error.response.data.errors;
                    }
                })
            }
        }
    }
</script>

<style scoped>
.vdatetime-input {
    @apply text-center;
}
</style>